import { useCallback, useEffect } from 'react';

interface UseKeyboardControlsOptions {
  skipBack?: () => void;
  skipForward?: () => void;
  enabled?: boolean;
}

export const useKeyboardControls = (
  videoRef: React.RefObject<HTMLVideoElement>,
  options: UseKeyboardControlsOptions = {}
) => {
  const { skipBack, skipForward, enabled = true } = options;

  // Detect if we're on desktop (not mobile)
  const isDesktop = useCallback(() => {
    return window.innerWidth > 767 && !/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  const handleKeydown = useCallback((event: KeyboardEvent) => {
    if (!enabled || !isDesktop() || !videoRef.current) return;

    // Don't handle keyboard events if user is typing in an input
    const activeElement = document.activeElement;
    if (activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      (activeElement as HTMLElement).isContentEditable
    )) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        skipBack?.();
        break;
      
      case 'ArrowRight':
        event.preventDefault();
        skipForward?.();
        break;
      
      case ' ':
      case 'Space':
        event.preventDefault();
        if (videoRef.current.paused) {
          videoRef.current.play();
        } else {
          videoRef.current.pause();
        }
        break;
    }
  }, [enabled, videoRef, skipBack, skipForward, isDesktop]);

  useEffect(() => {
    if (!enabled || !isDesktop()) return;

    // Add event listener to document to catch keyboard events globally
    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown, enabled, isDesktop]);

  return {
    isDesktop: isDesktop()
  };
}; 